import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";
import {
  TrendingUp,
  Boxes,
  Building2,
  ChefHat,
  Receipt,
  Loader2,
} from "lucide-react";
import {
  PageHeader,
  Card,
  Pill,
  SectionTitle,
} from "@/components/layout/PageShell";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { useReportBranch } from "@/contexts/ReportBranchContext";

import api from "@/lib/api";
import { aed, num } from "@/lib/format";
import { printBacklogReport } from "@/lib/backlogReportPrint";
import { displayItemName } from "@/lib/menuLocale";
import { cn } from "@/lib/utils";
import { ReportKpiStrip } from "@/components/reports/ReportKpiStrip";
import { ReportTableCard } from "@/components/reports/ReportTableCard";
import ReportExportButtons from "@/components/reports/ReportExportButtons";
import PaymentMethodFilter from "@/components/reports/PaymentMethodFilter";
import OrderTypeFilter from "@/components/reports/OrderTypeFilter";
import { formatReportFilterLine } from "@/lib/reportFilters";

const TABS = [
  { id: "categories", label: "Category Sales", path: "/reports/category-sales", icon: TrendingUp },
  { id: "top-items", label: "Top Items", path: "/reports/top-items", icon: Boxes },
  { id: "suppliers", label: "Supplier Summary", path: "/reports/supplier-summary", icon: Building2 },
  { id: "kitchen", label: "Kitchen", path: "/reports/kitchen", icon: ChefHat },
  { id: "orders", label: "Order Summary", path: "/reports/order-summary", icon: Receipt },
];

const ENDPOINTS = {
  categories: "/reports/backlog/categories",
  "top-items": "/reports/backlog/top-items",
  suppliers: "/reports/backlog/suppliers",
  kitchen: "/reports/backlog/kitchen",
  orders: "/reports/backlog/orders",
};

const monthStart = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
};

const today = () => new Date().toISOString().slice(0, 10);

export default function BacklogReports() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { branchId, allBranches, reportParams, canQuery } = useReportBranch();

  const activeTab = useMemo(() => {
    if (location.pathname.includes("top-items")) return "top-items";
    if (location.pathname.includes("supplier-summary")) return "suppliers";
    if (location.pathname.includes("kitchen")) return "kitchen";
    if (location.pathname.includes("order-summary")) return "orders";
    return "categories";
  }, [location.pathname]);

  const showOrderType = activeTab === "categories" || activeTab === "top-items" || activeTab === "orders";
  const showPaymentMethod = activeTab === "categories" || activeTab === "top-items" || activeTab === "orders";

  const [fromDate, setFromDate] = useState(monthStart);
  const [toDate, setToDate] = useState(today);
  const [orderType, setOrderType] = useState("all");
  const [paymentMethod, setPaymentMethod] = useState("all");
  const [topLimit, setTopLimit] = useState("25");

  const { data: report, isLoading, isFetching } = useQuery({
    queryKey: ["backlog-report", activeTab, branchId, fromDate, toDate, orderType, paymentMethod, topLimit],
    queryFn: async () => {
      const params = { ...reportParams, from_date: fromDate, to_date: toDate };
      if (showOrderType && orderType !== "all") params.order_type = orderType;
      if (showPaymentMethod && paymentMethod !== "all") params.payment_method = paymentMethod;
      if (activeTab === "top-items") params.limit = topLimit;

      const { data: res } = await api.get(ENDPOINTS[activeTab], { params });
      return res;
    },
    enabled: Boolean(canQuery && fromDate && toDate),
  });

  const title = TABS.find((t) => t.id === activeTab)?.label ?? "Reports";

  return (
    <div className="min-w-0">
      <PageHeader
        title={title}
        subtitle="Prioritised operational reports from the legacy backlog"
        actions={
          <ReportExportButtons
            disabled={!report}
            title={title}
            filename={`backlog-${activeTab}`}
            period={formatReportFilterLine({
              fromDate,
              toDate,
              orderType: showOrderType ? orderType : "all",
              paymentMethod: showPaymentMethod ? paymentMethod : "all",
            })}
            onPrint={() => printBacklogReport(report, activeTab, { branchId, allBranches })}
          />
        }
      />

      <div className="flex gap-1 mb-4 border-b border-slate-200 overflow-x-auto">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => navigate(tab.path)}
              className={cn(
                "flex items-center gap-1.5 px-4 py-2.5 text-[12.5px] font-medium border-b-2 -mb-px whitespace-nowrap",
                activeTab === tab.id ? "border-blue-600 text-blue-700" : "border-transparent text-slate-600 hover:text-slate-900",
              )}
            >
              <Icon size={14} /> {tab.label}
            </button>
          );
        })}
      </div>

      <Card className="p-5 mb-4">
        <SectionTitle className="!mb-3">Filters</SectionTitle>
        <div className="flex flex-wrap items-end gap-4">
          <>
            <div>
              <Label className="text-[12px] text-slate-600 mb-1 block">From</Label>
              <Input type="date" className="h-9 w-[160px]" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
            </div>
            <div>
              <Label className="text-[12px] text-slate-600 mb-1 block">To</Label>
              <Input type="date" className="h-9 w-[160px]" value={toDate} onChange={(e) => setToDate(e.target.value)} />
            </div>
          </>
          {showOrderType && (
            <OrderTypeFilter value={orderType} onChange={setOrderType} />
          )}
          {showPaymentMethod && (
            <PaymentMethodFilter value={paymentMethod} onChange={setPaymentMethod} />
          )}
          {activeTab === "top-items" && (
            <div>
              <Label className="text-[12px] text-slate-600 mb-1 block">Top N</Label>
              <Select value={topLimit} onValueChange={setTopLimit}>
                <SelectTrigger className="h-9 w-[100px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["10", "25", "50"].map((n) => (
                    <SelectItem key={n} value={n}>{n}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {(isLoading || isFetching) && (
            <Pill className="mb-1"><Loader2 size={12} className="animate-spin mr-1" /> Loading…</Pill>
          )}
        </div>
      </Card>

      {report && activeTab === "categories" && <CategoryView report={report} />}
      {report && activeTab === "top-items" && <TopItemsView report={report} />}
      {report && activeTab === "suppliers" && <SupplierView report={report} />}
      {report && activeTab === "kitchen" && <KitchenView report={report} />}
      {report && activeTab === "orders" && <OrderView report={report} />}
    </div>
  );
}

function CategoryView({ report }) {
  return (
    <>
      <ReportKpiStrip
        items={[
          { label: "Categories", value: num(report.summary?.category_count) },
          { label: "Round off", value: aed(report.summary?.round_off) },
          { label: "Net sales", value: aed(report.summary?.net_sales), emphasize: true },
        ]}
      />
      <ReportTableCard title="By category">
        <table>
          <thead>
            <tr>
              <th className="text-left">Category</th>
              <th className="text-left">Main menu</th>
              <th className="text-right">Items</th>
              <th className="text-right">Qty</th>
              <th className="text-right">Gross</th>
              <th className="text-right">Net</th>
            </tr>
          </thead>
          <tbody>
            {(report.rows || []).map((r) => (
              <tr key={r.sub_menu_id ?? r.category_name}>
                <td>{r.category_name}</td>
                <td className="text-slate-600">{r.main_menu_name || "—"}</td>
                <td className="text-right">{r.item_count}</td>
                <td className="text-right">{num(r.qty)}</td>
                <td className="text-right font-mono">{aed(r.gross_sales)}</td>
                <td className="text-right font-mono font-medium">{aed(r.net_sales)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </ReportTableCard>
    </>
  );
}

function TopItemsView({ report }) {
  return (
    <>
      <ReportKpiStrip
        items={[
          { label: "Items", value: num(report.summary?.item_count) },
          { label: "Total qty", value: num(report.summary?.total_qty) },
          { label: "Round off", value: aed(report.summary?.round_off) },
          { label: "Net sales", value: aed(report.summary?.net_sales), emphasize: true },
        ]}
      />
      <ReportTableCard title={`Top ${report.limit} by quantity`}>
      <table>
        <thead>
          <tr>
            <th className="text-left w-12">#</th>
            <th className="text-left">Code</th>
            <th className="text-left">Item</th>
            <th className="text-right">Qty</th>
            <th className="text-right">Net</th>
          </tr>
        </thead>
        <tbody>
          {(report.rows || []).map((r) => (
            <tr key={r.menu_item_id}>
              <td className="font-medium text-slate-500">{r.rank}</td>
              <td className="font-mono text-[12px]">{r.item_code}</td>
              <td>{displayItemName(r)}</td>
              <td className="text-right font-medium">{num(r.qty)}</td>
              <td className="text-right font-mono">{aed(r.net_sales)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </ReportTableCard>
    </>
  );
}

function SupplierView({ report }) {
  return (
    <>
      <ReportKpiStrip
        items={[
          { label: "Suppliers", value: num(report.summary?.supplier_count) },
          { label: "With balance", value: num(report.summary?.with_balance) },
          { label: "Period purchases", value: aed(report.summary?.period_purchases) },
          { label: "Period payments", value: aed(report.summary?.period_payments) },
          { label: "Outstanding", value: aed(report.summary?.total_outstanding), emphasize: true },
        ]}
      />
      <ReportTableCard title="Suppliers">
        <table>
          <thead>
            <tr>
              <th className="text-left">Code</th>
              <th className="text-left">Supplier</th>
              <th className="text-left">Contact</th>
              <th className="text-right">Purchases</th>
              <th className="text-right">Payments</th>
              <th className="text-right">Pay count</th>
              <th className="text-right">Limit</th>
              <th className="text-right">Balance</th>
            </tr>
          </thead>
          <tbody>
            {(report.rows || []).map((r) => (
              <tr key={r.supplier_id} className={cn(r.over_limit && "bg-red-50/50")}>
                <td className="font-mono text-[12px]">{r.supplier_code}</td>
                <td>{r.supplier_name}</td>
                <td className="text-slate-600">{r.contact_person || r.phone || "—"}</td>
                <td className="text-right font-mono">{aed(r.purchase_amount)}</td>
                <td className="text-right font-mono">{aed(r.payment_amount)}</td>
                <td className="text-right">{r.payment_count}</td>
                <td className="text-right font-mono">{aed(r.credit_limit)}</td>
                <td className="text-right font-mono font-medium">{aed(r.balance)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </ReportTableCard>
    </>
  );
}

function KitchenView({ report }) {
  const s = report.summary || {};
  return (
    <>
      <ReportKpiStrip
        items={[
          { label: "KOT tickets", value: num(s.ticket_count) },
          { label: "Lines", value: num(s.line_count) },
          { label: "Served", value: num(s.served_count), emphasize: true },
          { label: "Avg prep (min)", value: s.avg_prep_minutes ?? "—" },
        ]}
      />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-3">
        <ReportTableCard title="By station" className="mb-0">
          <table>
            <thead>
              <tr>
                <th className="text-left">Station</th>
                <th className="text-right">Tickets</th>
                <th className="text-right">Served</th>
                <th className="text-right">Avg min</th>
              </tr>
            </thead>
            <tbody>
              {(report.by_station || []).map((r) => (
                <tr key={r.station}>
                  <td>{r.station}</td>
                  <td className="text-right">{r.ticket_count}</td>
                  <td className="text-right">{r.served_count}</td>
                  <td className="text-right">{r.avg_prep_minutes ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </ReportTableCard>
        <ReportTableCard title="By status" className="mb-0">
          <table>
            <thead>
              <tr>
                <th className="text-left">Status</th>
                <th className="text-right">Tickets</th>
              </tr>
            </thead>
            <tbody>
              {(report.by_status || []).map((r) => (
                <tr key={r.status}>
                  <td>{r.status}</td>
                  <td className="text-right">{r.ticket_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </ReportTableCard>
      </div>
    </>
  );
}

function OrderView({ report }) {
  const s = report.summary || {};
  return (
    <>
      <ReportKpiStrip
        items={[
          { label: "Total orders", value: num(s.total_orders) },
          { label: "Open", value: num(s.open_orders) },
          { label: "Held", value: num(s.held_orders) },
          { label: "Paid", value: num(s.paid_orders) },
          { label: "Round off", value: aed(s.round_off) },
          { label: "Paid net", value: aed(s.paid_net_sales), emphasize: true },
        ]}
      />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <ReportTableCard title="By status" className="mb-0">
          <table>
            <thead>
              <tr>
                <th className="text-left">Status</th>
                <th className="text-right">Orders</th>
                <th className="text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {(report.by_status || []).map((r) => (
                <tr key={r.status}>
                  <td className="capitalize">{r.status}</td>
                  <td className="text-right">{r.order_count}</td>
                  <td className="text-right font-mono">{aed(r.net_amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </ReportTableCard>
        <ReportTableCard title="By order type" className="mb-0">
          <table>
            <thead>
              <tr>
                <th className="text-left">Type</th>
                <th className="text-right">Orders</th>
                <th className="text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {(report.by_order_type || []).map((r) => (
                <tr key={r.order_type}>
                  <td>{r.order_type_label}</td>
                  <td className="text-right">{r.order_count}</td>
                  <td className="text-right font-mono">{aed(r.net_amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </ReportTableCard>
      </div>
    </>
  );
}
